import { useRef } from 'react';

import { Autocomplete, Chip, FormControl, FormHelperText } from '@mui/material';

import { StyledTextField } from '../style';

interface MultiSelectFieldProps {
  id: string;
  label: string;
  value: any;
  options?: { label: string; value: string | number }[];
  loading?: boolean;
  required?: boolean;
  error?: string | boolean;
  helperText?: string;
  placeholder?: string;
  isLast?: boolean;
  onChange: (value: any) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  inputRef?: (el: HTMLElement | null) => void;
}

export const MultiSelectField = ({
  id,
  label,
  value,
  options = [],
  loading,
  required,
  error,
  helperText,
  isLast = true,
  onChange,
  onKeyDown,
  inputRef: externalInputRef,
}: MultiSelectFieldProps) => {
  const localInputRef = useRef<HTMLInputElement | null>(null);

  // Convert value to array if needed
  const valueArray = Array.isArray(value) ? value : value ? [value] : [];

  // Map values to option objects
  const selectedOptions = valueArray
    .map((val) => options.find((opt) => opt.value === val))
    .filter(Boolean) as { label: string; value: string | number }[];

  // Handle special key press for multi-select
  const handleMultiSelectKeyDown = (e: React.KeyboardEvent, params: any) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      const inputValue = params.inputProps.value;
      if (inputValue && inputValue.toString().trim() !== '') {
        // Try to find matching option by label
        const matchedOption = options.find(
          (opt) =>
            opt.label.toLowerCase() === inputValue.toString().toLowerCase(),
        );

        if (matchedOption) {
          // Add the option if not already selected
          const currentValues = Array.isArray(value)
            ? value
            : value
            ? [value]
            : [];
          if (!currentValues.includes(matchedOption.value)) {
            onChange([...currentValues, matchedOption.value]);
          }

          // Clear input but keep focus on this field
          params.inputProps.onChange?.({ target: { value: '' } } as any);
          e.preventDefault();
        }
      }
    }

    onKeyDown(e);
  };

  return (
    <FormControl
      error={!!error}
      sx={{
        '& .MuiFormControl-root': { height: '100% !important' },
        height: '100% !important',
      }}
    >
      <Autocomplete
        id={id}
        style={{ flex: 1, height: '100%' }}
        value={selectedOptions}
        loading={loading}
        disablePortal
        multiple
        autoSelect
        autoHighlight
        options={options}
        limitTags={2}
        popupIcon={null} // Remove dropdown arrow icon
        noOptionsText="Not found"
        getOptionLabel={(option) => option?.label || ''}
        isOptionEqualToValue={(option, val) => {
          return Boolean(option && val && option.value === val.value);
        }}
        onChange={(_, newValues) => {
          onChange(newValues.map((val) => val?.value));
        }}
        ListboxProps={{ style: { maxHeight: 340 } }}
        sx={{
          '& .MuiAutocomplete-tag': {
            margin: '2px',
          },
          '& .MuiAutocomplete-root': {
            height: '100%', // Fixed height
          },
          '& .MuiAutocomplete-inputRoot': {
            flexWrap: 'wrap',
            // minHeight: '60px', // Ensure consistent height
            height: '100%', // Fixed height
          },
          height: '100%',
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const tagProps = getTagProps({ index });
            const { key, ...restTagProps } = tagProps; // Exclude key from tagProps
            return (
              <Chip
                key={index}
                label={option?.label || ''}
                size="small"
                {...restTagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <StyledTextField
            variant="filled"
            {...params}
            fullWidth
            label={label}
            required={required}
            onKeyDown={(e) => handleMultiSelectKeyDown(e, params)}
            inputRef={(el) => {
              localInputRef.current = el;
              if (externalInputRef) externalInputRef(el);
            }}
            sx={{
              height: '100%',
              '& .MuiFilledInput-root': {
                height: '100%',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderLeft: 'none',
                ...(!isLast && {
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderRight: 'none',
                }),
                // Hide dropdown indicator space
                paddingRight: '12px',
              },
              // Ensure no extra padding is added for the missing arrow
              '& .MuiAutocomplete-endAdornment': {
                display: 'none',
              },
            }}
          />
        )}
      />
      {(error || helperText) && (
        <FormHelperText>
          {typeof error === 'string' ? error : helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
